import { TransactionsStatus } from "../utils/commons";
import type { ITransaction } from "../interfaces/transactions";
import type { EstimateOption } from "../interfaces/payout";
import type { ICardPaymentMethod } from "../interfaces/payment-method";
import type { FundingMethod } from "../utils/sendCatalog";
import type {
  IComposeDraft,
  IDeferredMintResult,
  IOriginationDraft,
} from "../store/useDeferredOriginationStore";

export const DEFERRED_ORIGINATION_MOCK_FX = 17.42;

/** Representative pre-quote options keyed by funding method (send-side). */
export const deferredOriginationEstimateOptions: EstimateOption[] = [
  {
    capabilityKey: "US-MX-DEBIT_CARD-BANK_ACCOUNT",
    corridorCode: "US-MX",
    payerCode: null,
    fundingMethod: "DEBIT_CARD",
    payoutMethod: "BANK_ACCOUNT",
    sendAmount: 100,
    receiveAmount: 1742,
    customerFee: 1,
    customerFxRate: DEFERRED_ORIGINATION_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  {
    capabilityKey: "US-MX-CASH_DEPOSIT-BANK_ACCOUNT",
    corridorCode: "US-MX",
    payerCode: null,
    fundingMethod: "CASH_DEPOSIT",
    payoutMethod: "BANK_ACCOUNT",
    sendAmount: 100,
    receiveAmount: 1742,
    customerFee: 0,
    customerFxRate: DEFERRED_ORIGINATION_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  {
    capabilityKey: "US-MX-ACH-BANK_ACCOUNT",
    corridorCode: "US-MX",
    payerCode: null,
    fundingMethod: "ACH",
    payoutMethod: "BANK_ACCOUNT",
    sendAmount: 100,
    receiveAmount: 1742,
    customerFee: 0.5,
    customerFxRate: DEFERRED_ORIGINATION_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
];

export const deferredOriginationFundingMethods = [
  "DEBIT_CARD",
  "CASH_DEPOSIT",
  "ACH",
] as const;

export interface DeferredFundingMethodDetails {
  fee: number | null;
  minAmount: number | null;
  maxAmount: number | null;
}

/** Distinct per-method send bounds for the gallery funding picker (no network). */
export const deferredOriginationFundingDetails: Partial<
  Record<FundingMethod, DeferredFundingMethodDetails>
> = {
  DEBIT_CARD: { fee: 1, minAmount: 10, maxAmount: 200 },
  CASH_DEPOSIT: { fee: 0, minAmount: 25, maxAmount: 500 },
  ACH: { fee: 0.5, minAmount: null, maxAmount: 2500 },
};

export const deferredOriginationSavedCards: ICardPaymentMethod[] = [
  {
    paymentMethodId: "pm_deferred_visa",
    brand: "visa",
    last4: "4242",
    expiryMonth: "12",
    expiryYear: "2030",
    cardholderName: "Alex Sender",
    enabled: true,
    isDefault: true,
    fundingMethod: "DEBIT_CARD",
  },
  {
    paymentMethodId: "pm_deferred_mc",
    brand: "mastercard",
    last4: "4444",
    expiryMonth: "08",
    expiryYear: "2028",
    cardholderName: "Alex Sender",
    enabled: true,
    isDefault: false,
    fundingMethod: "CREDIT_CARD",
  },
];

export const deferredOriginationRecentRecipients = [
  { fullName: "Maria Lopez", timesUsed: 3 },
  { fullName: "Juan Perez", timesUsed: 1 },
  { fullName: "Ana Ruiz", timesUsed: 2 },
];

export const deferredOriginationMockComposeDraft: IComposeDraft = {
  sourceCountry: "US",
  destinationCountry: "MX",
  sourceCurrency: "USD",
  destinationCurrency: "MXN",
  sendAmount: 100,
  totalDebit: 101,
  fundingMethod: "DEBIT_CARD",
  capabilityKey: "US-MX-DEBIT_CARD-BANK_ACCOUNT",
  fundingPaymentMethodId: "pm_deferred_visa",
};

export const deferredOriginationMockDraft: IOriginationDraft = {
  ...deferredOriginationMockComposeDraft,
  recipientName: "Maria Lopez",
};

export const deferredOriginationMockMintResult: IDeferredMintResult = {
  claimUrl: "https://pay.leap.example/claim/dpr-gallery-ready",
  originationId: "dpr-gallery-ready",
  expiresAt: "2026-08-01T00:00:00.000Z",
  senderFirstName: "Jane",
};

/** Mock originations for the deferred payout sender hub. */
export const deferredOriginationMockList: ITransaction[] = [
  {
    originationId: "dpr-gallery-pending",
    status: TransactionsStatus.PENDING,
    amount: "1,000.00",
    currency: "USD",
    date: "05/07/26",
    recipientName: "Maria Lopez",
  },
  {
    originationId: "dpr-gallery-funded",
    status: TransactionsStatus.COMPLETED,
    operationStatus: "completed",
    operationId: "op_gallery_deferred_1",
    amount: "1,000.00",
    currency: "USD",
    date: "02/07/26",
    recipientName: "Juan Perez",
  },
  {
    originationId: "dpr-gallery-cash",
    status: TransactionsStatus.PENDING,
    amount: "75.00",
    currency: "USD",
    date: "28/06/26",
    recipientName: "Ana Ruiz",
    cashDepositPending: true,
  },
];
